import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Table, Card, Badge, Spinner } from 'react-bootstrap';
import { Wand2, Plus, Trash2, FileText, CheckCircle, Printer } from 'lucide-react';
import { parseQuoteText, createQuote, updateQuote, convertQuoteToTrips, getVehicleTypes } from '../api';
import { generateQuotePDFPrintWindow } from './QuotePDFGenerator';
import { toast } from 'react-toastify';

const QuoteEditorModal = ({ show, onHide, quote, onSaved }) => {
    const [rawText, setRawText] = useState('');
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [converting, setConverting] = useState(false);
    const [systemVehicleTypes, setSystemVehicleTypes] = useState([]);

    // Form Header State
    const [clientName, setClientName] = useState('');
    const [clientContact, setClientContact] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [currency, setCurrency] = useState('CLP');
    const [markupPercentage, setMarkupPercentage] = useState(30.0);
    const [notesIncluded, setNotesIncluded] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [status, setStatus] = useState('DRAFT');

    // Items State
    const [items, setItems] = useState([
        {
            item_order: 1,
            date: '',
            time_str: '',
            origin: '',
            destination: '',
            vehicle_type_name: 'Van Standard',
            pax_count: 1,
            service_type: 'transfer',
            supplier_cost: 0,
            markup_percentage: 30,
            sale_price: 0,
            notes: ''
        }
    ]);

    // Cargar tipos de vehículos del sistema
    useEffect(() => {
        const loadVT = async () => {
            try {
                const types = await getVehicleTypes();
                if (Array.isArray(types)) {
                    setSystemVehicleTypes(types);
                }
            } catch (e) {
                console.error("Error al cargar tipos de vehículos:", e);
            }
        };
        if (show) {
            loadVT();
        }
    }, [show]);

    useEffect(() => {
        if (quote) {
            setClientName(quote.client_name || '');
            setClientContact(quote.client_contact || '');
            setClientEmail(quote.client_email || '');
            setClientPhone(quote.client_phone || '');
            setCurrency(quote.currency || 'CLP');
            setMarkupPercentage(quote.markup_percentage || 30.0);
            setNotesIncluded(quote.notes_included || '');
            setInternalNotes(quote.internal_notes || '');
            setStatus(quote.status || 'DRAFT');
            setRawText(quote.raw_request_text || '');
            
            if (quote.items && quote.items.length > 0) {
                setItems(quote.items.map(it => ({
                    ...it,
                    date: it.date ? it.date.split('T')[0] : ''
                })));
            }
        } else {
            resetForm();
        }
    }, [quote, show]);

    const resetForm = () => {
        setClientName('');
        setClientContact('');
        setClientEmail('');
        setClientPhone('');
        setCurrency('CLP');
        setMarkupPercentage(30.0);
        setNotesIncluded('');
        setInternalNotes('');
        setStatus('DRAFT');
        setRawText('');
        setItems([
            {
                item_order: 1,
                date: '',
                time_str: '',
                origin: '',
                destination: '',
                vehicle_type_name: 'Van Standard',
                pax_count: 1,
                service_type: 'transfer',
                supplier_cost: 0,
                markup_percentage: 30,
                sale_price: 0,
                notes: ''
            }
        ]);
    };

    // Parse Magic Text
    const handleParseText = async () => {
        if (!rawText.trim()) {
            toast.warn("Por favor pega primero el texto de la cotización en la caja.");
            return;
        }
        setParsing(true);
        try {
            const res = await parseQuoteText(rawText);
            if (res.client_name && !clientName) setClientName(res.client_name);
            if (res.notes_included) {
                setNotesIncluded(prev => prev ? `${prev}, ${res.notes_included}` : res.notes_included);
            }
            if (res.items && res.items.length > 0) {
                setItems(res.items.map((it, idx) => ({
                    ...it,
                    item_order: idx + 1,
                    markup_percentage: Math.max(30, markupPercentage),
                    date: it.date ? it.date.split('T')[0] : ''
                })));
            }
            toast.success("✨ Texto parseado y estructurado con éxito.");
        } catch (e) {
            console.error(e);
            toast.error("Error al estructurar el texto.");
        }
        setParsing(false);
    };

    const handleSelectSystemVehicle = (index, vtId) => {
        const vt = systemVehicleTypes.find(v => String(v.id) === String(vtId));
        if (!vt) return;
        const updated = [...items];
        updated[index].vehicle_type_name = vt.name;
        if (vt.capacity) {
            updated[index].pax_count = vt.capacity;
        }
        setItems(updated);
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;

        // Auto calcular precio venta si cambia costo o margen
        if (field === 'supplier_cost' || field === 'markup_percentage') {
            const cost = parseFloat(field === 'supplier_cost' ? value : updated[index].supplier_cost) || 0;
            let mk = parseFloat(field === 'markup_percentage' ? value : updated[index].markup_percentage) || 30.0;
            if (mk < 30.0) mk = 30.0; // Garantizar margen minimo 30%
            updated[index].markup_percentage = mk;
            updated[index].sale_price = cost > 0 ? Math.round(cost * (1 + mk / 100)) : 0;
        }

        setItems(updated);
    };

    const addItem = () => {
        const defaultVt = systemVehicleTypes.length > 0 ? systemVehicleTypes[0].name : 'Van Standard';
        const defaultCap = systemVehicleTypes.length > 0 && systemVehicleTypes[0].capacity ? systemVehicleTypes[0].capacity : 1;
        setItems([
            ...items,
            {
                item_order: items.length + 1,
                date: '',
                time_str: '',
                origin: '',
                destination: '',
                vehicle_type_name: defaultVt,
                pax_count: defaultCap,
                service_type: 'transfer',
                supplier_cost: 0,
                markup_percentage: Math.max(30, markupPercentage),
                sale_price: 0,
                notes: ''
            }
        ]);
    };

    const removeItem = (index) => {
        if (items.length <= 1) return;
        const updated = items.filter((_, i) => i !== index).map((it, i) => ({ ...it, item_order: i + 1 }));
        setItems(updated);
    };

    // Totales calculados
    const totalCost = items.reduce((sum, it) => sum + (parseFloat(it.supplier_cost) || 0), 0);
    const totalPrice = items.reduce((sum, it) => sum + (parseFloat(it.sale_price) || 0), 0);
    const profit = totalPrice - totalCost;
    const profitMargin = totalPrice > 0 ? ((profit / totalPrice) * 100).toFixed(1) : '0.0';

    const handleSave = async (saveStatus = status) => {
        if (!clientName.trim()) {
            toast.warn("Ingresa el Nombre del Cliente o Empresa.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                client_name: clientName.trim(),
                client_contact: clientContact.trim() || undefined,
                client_email: clientEmail.trim() || undefined,
                client_phone: clientPhone.trim() || undefined,
                currency,
                markup_percentage: Math.max(30, parseFloat(markupPercentage) || 30.0),
                is_exempt: true,
                status: saveStatus,
                raw_request_text: rawText || undefined,
                notes_included: notesIncluded || undefined,
                internal_notes: internalNotes || undefined,
                items: items.map(it => ({
                    ...it,
                    supplier_cost: parseFloat(it.supplier_cost) || 0,
                    markup_percentage: Math.max(30, parseFloat(it.markup_percentage) || 30.0),
                    sale_price: parseFloat(it.sale_price) || 0
                }))
            };

            let savedObj;
            if (quote && quote.id) {
                savedObj = await updateQuote(quote.id, payload);
                toast.success(`✅ Cotización #${savedObj.quote_number} actualizada con éxito.`);
            } else {
                savedObj = await createQuote(payload);
                toast.success(`✅ Cotización #${savedObj.quote_number} creada con éxito.`);
            }

            if (onSaved) onSaved(savedObj);
            onHide();
        } catch (e) {
            console.error(e);
            toast.error(`⚠️ Error al guardar cotización: ${e.response?.data?.detail || e.message}`);
        }
        setSaving(false);
    };

    const handleConvertToTrips = async () => {
        if (!quote || !quote.id) {
            toast.warn("Primero debes guardar la cotización para poder convertirla en viajes.");
            return;
        }
        if (!window.confirm(`¿Estás seguro de convertir la Cotización #${quote.quote_number} en reservas reales en el Despacho Diario?`)) {
            return;
        }
        setConverting(true);
        try {
            const res = await convertQuoteToTrips(quote.id);
            toast.success(`🎉 ${res.message}`);
            if (onSaved) onSaved();
            onHide();
        } catch (e) {
            console.error(e);
            toast.error(`⚠️ Error al convertir: ${e.response?.data?.detail || e.message}`);
        }
        setConverting(false);
    };

    const handlePrintPDF = () => {
        const fullQuoteObj = {
            id: quote ? quote.id : 0,
            quote_number: quote ? quote.quote_number : 'COT-DRAFT',
            client_name: clientName || 'Cliente Evento',
            client_contact: clientContact,
            client_email: clientEmail,
            client_phone: clientPhone,
            currency,
            total_cost: totalCost,
            total_price: totalPrice,
            created_at: quote ? quote.created_at : new Date().toISOString(),
            notes_included: notesIncluded,
            items
        };
        generateQuotePDFPrintWindow(fullQuoteObj);
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                    <FileText className="text-primary" size={24} />
                    {quote ? `Editar Cotización #${quote.quote_number}` : 'Nueva Cotización de Evento / Servicio Especial'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 space-y-4">

                {/* CAJA MÁGICA: PARSER DE WHATSAPP */}
                <Card className="border-primary border-2 bg-light shadow-sm">
                    <Card.Header className="bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                        <span className="fw-bold small d-flex align-items-center gap-1">
                            <Wand2 size={16} /> 🪄 IMPORTADOR INTELIGENTE (PEGAR WHATSAPP)
                        </span>
                        <Badge bg="warning" text="dark" className="fw-bold">IA / Parser V1</Badge>
                    </Card.Header>
                    <Card.Body className="p-3">
                        <Form.Group>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Pega aquí el mensaje del cliente recibido por WhatsApp (ej: 'Hola, cotizar Sprinter para 15 pax el 10 de agosto desde Santiago a Colbún con 2h de espera...')"
                                value={rawText}
                                onChange={(e) => setRawText(e.target.value)}
                                className="font-monospace text-dark mb-2"
                                style={{ fontSize: '12px' }}
                            />
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">El sistema extraerá automáticamente fechas, pax, tramos y requerimientos especiales.</small>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    className="fw-bold" 
                                    onClick={handleParseText} 
                                    disabled={parsing || !rawText.trim()}
                                >
                                    {parsing ? <Spinner size="sm" animation="border" /> : <><Wand2 size={14} className="me-1"/> Auto-Estructurar Cotización</>}
                                </Button>
                            </div>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* DATOS DEL CLIENTE Y CONFIGURACIÓN */}
                <Row className="g-3">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Cliente / Empresa *</Form.Label>
                            <Form.Control 
                                type="text" 
                                size="sm" 
                                placeholder="Ej: Minera Pelambres" 
                                value={clientName} 
                                onChange={e => setClientName(e.target.value)} 
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Contacto / Solicitante</Form.Label>
                            <Form.Control 
                                type="text" 
                                size="sm" 
                                placeholder="Ej: Gonzalo Fernández" 
                                value={clientContact} 
                                onChange={e => setClientContact(e.target.value)} 
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Teléfono</Form.Label>
                            <Form.Control 
                                type="text" 
                                size="sm" 
                                placeholder="+56 9..." 
                                value={clientPhone} 
                                onChange={e => setClientPhone(e.target.value)} 
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Moneda</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={currency} 
                                onChange={e => setCurrency(e.target.value)}
                                className="fw-bold"
                            >
                                <option value="CLP">🇨🇱 CLP ($)</option>
                                <option value="USD">🇺🇸 USD (u$s)</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Margen Mínimo (%)</Form.Label>
                            <Form.Control 
                                type="number" 
                                size="sm" 
                                min="30"
                                value={markupPercentage} 
                                onChange={e => setMarkupPercentage(Math.max(30, parseFloat(e.target.value) || 30))} 
                                className="fw-bold text-success border-success"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* TABLA DE TRAMOS E ITINERARIOS */}
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                        <h6 className="fw-bold mb-0">🚘 Tramos del Servicio / Itinerario Multi-día</h6>
                        <Button variant="outline-primary" size="sm" onClick={addItem} className="fw-bold">
                            <Plus size={14} className="me-1"/> Agregar Tramo
                        </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table responsive hover className="align-middle mb-0" style={{ minWidth: '1000px' }}>
                            <thead className="bg-light">
                                <tr>
                                    <th style={{ width: '35px' }} className="ps-3">#</th>
                                    <th style={{ width: '120px' }}>Fecha</th>
                                    <th style={{ width: '85px' }}>Hora</th>
                                    <th>Origen</th>
                                    <th>Destino</th>
                                    <th style={{ width: '160px' }}>Categoría Sistema</th>
                                    <th style={{ width: '160px' }}>Nombre / Alias Cotización</th>
                                    <th style={{ width: '65px' }}>Pax</th>
                                    <th style={{ width: '100px' }}>Servicio</th>
                                    <th style={{ width: '100px' }}>Costo Prov.</th>
                                    <th style={{ width: '75px' }}>% Margen</th>
                                    <th style={{ width: '100px' }}>Venta</th>
                                    <th style={{ width: '35px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="ps-3 fw-bold text-muted">{index + 1}</td>
                                        <td>
                                            <Form.Control 
                                                type="date" 
                                                size="sm" 
                                                value={item.date || ''} 
                                                onChange={e => handleItemChange(index, 'date', e.target.value)} 
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="text" 
                                                size="sm" 
                                                placeholder="15:00" 
                                                value={item.time_str || ''} 
                                                onChange={e => handleItemChange(index, 'time_str', e.target.value)} 
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="text" 
                                                size="sm" 
                                                placeholder="Origen..." 
                                                value={item.origin || ''} 
                                                onChange={e => handleItemChange(index, 'origin', e.target.value)} 
                                                required
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="text" 
                                                size="sm" 
                                                placeholder="Destino..." 
                                                value={item.destination || ''} 
                                                onChange={e => handleItemChange(index, 'destination', e.target.value)} 
                                                required
                                            />
                                        </td>
                                        <td>
                                            <Form.Select 
                                                size="sm" 
                                                onChange={e => handleSelectSystemVehicle(index, e.target.value)}
                                                defaultValue=""
                                            >
                                                <option value="" disabled>Seleccionar...</option>
                                                {systemVehicleTypes.map(vt => (
                                                    <option key={vt.id} value={vt.id}>
                                                        {vt.name} ({vt.capacity || 4} pax)
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="text" 
                                                size="sm" 
                                                placeholder="Ej: Sprinter 15 pax" 
                                                value={item.vehicle_type_name || ''} 
                                                onChange={e => handleItemChange(index, 'vehicle_type_name', e.target.value)} 
                                                className="fw-bold"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="number" 
                                                size="sm" 
                                                min="1"
                                                value={item.pax_count || 1} 
                                                onChange={e => handleItemChange(index, 'pax_count', parseInt(e.target.value) || 1)} 
                                                className="text-center fw-bold"
                                            />
                                        </td>
                                        <td>
                                            <Form.Select 
                                                size="sm" 
                                                value={item.service_type} 
                                                onChange={e => handleItemChange(index, 'service_type', e.target.value)}
                                            >
                                                <option value="transfer">Transfer</option>
                                                <option value="full_day">Full Day</option>
                                                <option value="wait">Con Espera</option>
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="number" 
                                                size="sm" 
                                                placeholder="Costo" 
                                                value={item.supplier_cost || ''} 
                                                onChange={e => handleItemChange(index, 'supplier_cost', e.target.value)} 
                                                className="fw-bold"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="number" 
                                                size="sm" 
                                                min="30"
                                                value={item.markup_percentage || 30} 
                                                onChange={e => handleItemChange(index, 'markup_percentage', e.target.value)} 
                                                className="fw-bold text-success"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                type="number" 
                                                size="sm" 
                                                value={item.sale_price || ''} 
                                                onChange={e => handleItemChange(index, 'sale_price', e.target.value)} 
                                                className="fw-bold text-primary"
                                            />
                                        </td>
                                        <td>
                                            <Button 
                                                variant="link" 
                                                className="text-danger p-0" 
                                                onClick={() => removeItem(index)}
                                                disabled={items.length <= 1}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* EXTRAS E INCLUIDOS */}
                <Row className="g-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Servicios Incluidos / Notas al Cliente</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={2} 
                                placeholder="Ej: Incluye botellas de agua 500ml, snacks por persona, peajes y 2 horas de espera."
                                value={notesIncluded} 
                                onChange={e => setNotesIncluded(e.target.value)} 
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted text-uppercase">Notas Internas para Operación</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={2} 
                                placeholder="Ej: Proveedor Gonzalo reservado telefónicamente."
                                value={internalNotes} 
                                onChange={e => setInternalNotes(e.target.value)} 
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* RESUMEN FINANCIERO */}
                <Card className="bg-dark text-white p-3 border-0 shadow">
                    <Row className="align-items-center text-center">
                        <Col md={3}>
                            <div className="small text-muted text-uppercase fw-bold">Costo Proveedores</div>
                            <h4 className="fw-bold mb-0 text-light">
                                {currency === 'USD' ? `USD $${totalCost.toFixed(2)}` : `$${Math.round(totalCost).toLocaleString('es-CL')}`}
                            </h4>
                        </Col>
                        <Col md={3}>
                            <div className="small text-muted text-uppercase fw-bold">Precio Venta Cliente</div>
                            <h3 className="fw-black mb-0 text-primary">
                                {currency === 'USD' ? `USD $${totalPrice.toFixed(2)}` : `$${Math.round(totalPrice).toLocaleString('es-CL')}`}
                            </h3>
                            <small className="text-success fw-bold">(Exento de IVA)</small>
                        </Col>
                        <Col md={3}>
                            <div className="small text-muted text-uppercase fw-bold">Ganancia Estimada</div>
                            <h4 className="fw-bold mb-0 text-success">
                                {currency === 'USD' ? `USD $${profit.toFixed(2)}` : `$${Math.round(profit).toLocaleString('es-CL')}`}
                            </h4>
                        </Col>
                        <Col md={3}>
                            <div className="small text-muted text-uppercase fw-bold">Margen Real</div>
                            <Badge bg={parseFloat(profitMargin) >= 30 ? 'success' : 'warning'} className="px-3 py-2 fs-6">
                                {profitMargin}% MARGEN
                            </Badge>
                        </Col>
                    </Row>
                </Card>

            </Modal.Body>
            <Modal.Footer className="bg-light d-flex justify-content-between">
                <div>
                    <Button variant="outline-dark" size="sm" onClick={handlePrintPDF} className="fw-bold me-2">
                        <Printer size={16} className="me-1" /> Imprimir / PDF
                    </Button>
                    {quote && quote.id && (
                        <Button 
                            variant="success" 
                            size="sm" 
                            onClick={handleConvertToTrips} 
                            disabled={converting}
                            className="fw-bold"
                        >
                            {converting ? <Spinner size="sm" animation="border" /> : <><CheckCircle size={16} className="me-1"/> Convertir a Reservas</>}
                        </Button>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancelar</Button>
                    <Button variant="primary" onClick={() => handleSave('DRAFT')} disabled={saving} className="fw-bold">
                        {saving ? <Spinner size="sm" animation="border" /> : '💾 Guardar Cotización'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default QuoteEditorModal;
